import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import {
	ClientPurchase,
	CreateClientPurchasePayload,
	Purchase,
	UpdateClientPurchasePayload,
} from '@mira-avant/mtlibs';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class PurchaseService {
	private readonly BASE_PATH = 'purchases';
	public selectedPurchaseBs$ = new BehaviorSubject<ClientPurchase | undefined>(undefined);
	public readonly selectedPurchase$ = this.selectedPurchaseBs$.asObservable();
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public purchasesBs$: BehaviorSubject<ClientPurchase[]> = new BehaviorSubject<ClientPurchase[]>(
		[]
	);
	public purchases$: Observable<ClientPurchase[]> = this.purchasesBs$.pipe(
		filter(purchases => purchases !== null && purchases !== undefined)
	);

	constructor(private api: ApiService) {}

	public async getPurchaseTypes$(): Promise<Purchase[]> {
		return this.withLoading(async () => {
			const purchaseTypes = await firstValueFrom(
				this.api.get<Purchase[]>(this.BASE_PATH + '/all')
			);
			return purchaseTypes;
		});
	}

	public selectPurchase(purchase: ClientPurchase | undefined): void {
		this.selectedPurchaseBs$.next(purchase);
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async getPurchasesByClient$(client_uuid: string): Promise<ClientPurchase[]> {
		return this.withLoading(async () => {
			const purchases = await firstValueFrom(
				this.api.get<ClientPurchase[]>(`${this.BASE_PATH}/client/${client_uuid}`)
			);

			this.purchasesBs$.next(purchases);

			return purchases;
		});
	}

	public async createPurchase$(payload: CreateClientPurchasePayload): Promise<ClientPurchase> {
		return this.withLoading(async () => {
			const purchase = await firstValueFrom(
				this.api.post<ClientPurchase>(this.BASE_PATH, payload)
			);

			this.purchasesBs$.next([...this.purchasesBs$.value, purchase]);

			return purchase;
		});
	}

	public async updatePurchase$(
		uuid: string,
		payload: UpdateClientPurchasePayload
	): Promise<ClientPurchase> {
		return this.withLoading(async () => {
			const updatedPurchase = await firstValueFrom(
				this.api.put<ClientPurchase>(`${this.BASE_PATH}/${uuid}`, payload)
			);

			const currentPurchases = this.purchasesBs$.value;
			const index = currentPurchases.findIndex(p => p.uuid === uuid);

			if (index !== -1) {
				currentPurchases[index] = updatedPurchase;
				this.purchasesBs$.next([...currentPurchases]);
			}

			return updatedPurchase;
		});
	}
}
